import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Send, Edit2, X, Paperclip, FileText, Loader, Calendar, Clock, BarChart2, EyeOff, ChevronDown, BookOpen, Users, CheckCircle } from 'lucide-react';
import UnifiedHeader from '../../components/UnifiedHeader';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import {
  listAssignments, listCourses, getCourse,
  createAssignment, updateAssignment, togglePublishAssignment,
  uploadAssignmentAttachment, getCourseStudents
} from '../../services/studioApi';
import './Studio.css';

export function AssignmentForm({ courses, onSave, onClose, initial }) {
  const { language } = useLanguage();
  const tA = (translations[language]?.studio) || {};
  const tT = tA.teacher || {};

  const [form, setForm] = useState({
    course_id: initial?.course_id || (courses[0]?.id || ''),
    title: initial?.title || '',
    description: initial?.description || '',
    type: initial?.type || 'assignment',
    question_ids: Array.isArray(initial?.question_ids) ? initial.question_ids : [],
    time_limit_minutes: initial?.time_limit_minutes || '',
    due_date: initial?.due_date ? initial.due_date.slice(0, 16) : '',
    show_answers_after_submission: initial?.show_answers_after_submission || false,
    allow_multiple_attempts: initial?.allow_multiple_attempts !== false,
  });
  const [questions, setQuestions] = useState([]);
  const [loadingQ, setLoadingQ] = useState(false);
  const [attachments, setAttachments] = useState(initial?.attachments || []);
  // Student targeting
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [targetAll, setTargetAll] = useState(!initial?.student_ids?.length);
  const [selectedStudentIds, setSelectedStudentIds] = useState(
    Array.isArray(initial?.student_ids) ? initial.student_ids : []
  );
  const [studentSearch, setStudentSearch] = useState('');
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);


  useEffect(() => {
    if (!form.course_id) return;
    getCourse(form.course_id).then(c => {
      setLoadingQ(true);
      Promise.all((c.concepts || []).map(concept =>
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/studio/concepts/${concept.id}/questions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json()).catch(() => [])
      )).then(results => {
        setQuestions(results.flat().filter(q => q.is_approved));
      }).finally(() => setLoadingQ(false));
    }).catch(console.error);
    getCourseStudents(form.course_id).then(setEnrolledStudents).catch(() => setEnrolledStudents([]));
  }, [form.course_id]);

  const toggleQuestion = (qId) => {
    setForm(f => ({
      ...f,
      question_ids: f.question_ids.includes(qId)
        ? f.question_ids.filter(id => id !== qId)
        : [...f.question_ids, qId]
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const result = await uploadAssignmentAttachment(file);
      setAttachments(prev => [...prev, result]);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (url) => {
    setAttachments(prev => prev.filter(a => a.url !== url));
  };

  const canSave = form.title && form.question_ids.length > 0 && (targetAll || selectedStudentIds.length > 0);

  const detailsPanel = (
    <div className="studio-card" style={{ padding: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <FileText size={15} color="#5B35A0" /> {tT.assignmentDetails || 'Assignment Details'}
      </h3>

      <div className="form-group">
        <label className="form-label">{tT.selectCourse || 'Course'}</label>
        <select className="form-select" value={form.course_id}
          onChange={e => setForm(f => ({ ...f, course_id: e.target.value, question_ids: [] }))}>
          {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label className="form-label">{tT.assignmentTitle || 'Title'} *</label>
          <input className="form-input" value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder={tT.assignmentTitlePlaceholder || 'e.g. Chapter 3 Quiz'} />
        </div>
        <div className="form-group">
          <label className="form-label">{tT.assignmentType || 'Type'}</label>
          <select className="form-select" value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="assignment">{tT.assignmentTypeAssignment || 'Assignment'}</option>
            <option value="test">{tT.assignmentTypeTest || 'Test'}</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">{tT.dueDate || 'Due Date'}</label>
          <input type="datetime-local" className="form-input" value={form.due_date}
            onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{tT.timeLimit || 'Time Limit (minutes)'}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="number" className="form-input"
            value={form.time_limit_minutes}
            onChange={e => setForm(f => ({ ...f, time_limit_minutes: e.target.value ? parseInt(e.target.value) : '' }))}
            placeholder={tT.noLimit || 'No limit'}
            min={1} style={{ flex: 1 }} />
          {form.time_limit_minutes && (
            <button type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              onClick={() => setForm(f => ({ ...f, time_limit_minutes: '' }))}>
              {tT.setIndefinite || 'Clear'}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem', padding: '0.85rem 1rem', background: '#f8f9ff', borderRadius: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.show_answers_after_submission}
            onChange={e => setForm(f => ({ ...f, show_answers_after_submission: e.target.checked }))}
            style={{ accentColor: '#5B35A0' }} />
          {tT.showAnswers || 'Show answers after submission'}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.allow_multiple_attempts}
            onChange={e => setForm(f => ({ ...f, allow_multiple_attempts: e.target.checked }))}
            style={{ accentColor: '#5B35A0' }} />
          {tT.allowRetry || 'Allow multiple attempts'}
        </label>
      </div>

      {/* Attachments */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span><Paperclip size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{tT.attachments || 'Attachments'}</span>
          <button type="button" className="btn-secondary"
            style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}
            onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
            {uploadingFile ? <Loader size={12} className="spin" /> : <Paperclip size={12} />}
            {' '}{uploadingFile ? (tT.uploading || 'Uploading…') : (tT.addFile || 'Add File')}
          </button>
        </label>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileUpload}
          accept="image/*,.pdf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" />
        {attachments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
            {attachments.map((att, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.65rem', background: '#f8f9ff', borderRadius: 8, border: '1px solid #e8ecf0' }}>
                <FileText size={13} color="#5B35A0" style={{ flexShrink: 0 }} />
                <a href={att.url} target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, fontSize: '0.82rem', color: '#5B35A0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.name}
                </a>
                <span style={{ fontSize: '0.72rem', color: '#aaa', flexShrink: 0 }}>{att.size ? `${(att.size / 1024).toFixed(0)} KB` : ''}</span>
                <button type="button" onClick={() => removeAttachment(att.url)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', padding: 0, lineHeight: 1 }}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const studentPanel = (
    <div className="studio-card" style={{ padding: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Users size={15} color="#5B35A0" /> {tT.assignTo || 'Assign To'}
      </h3>
      {/* Toggle pills */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button type="button" onClick={() => { setTargetAll(true); setSelectedStudentIds([]); setStudentDropdownOpen(false); }}
          style={{ padding: '0.35rem 0.9rem', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: targetAll ? '#5B35A0' : '#f0f0f0', color: targetAll ? '#fff' : '#666', transition: 'all 0.15s' }}>
          {tT.allStudents || 'Everyone'}
        </button>
        <button type="button" onClick={() => { if (enrolledStudents.length > 0) setTargetAll(false); }}
          style={{ padding: '0.35rem 0.9rem', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600, cursor: enrolledStudents.length > 0 ? 'pointer' : 'not-allowed', border: 'none', background: !targetAll ? '#5B35A0' : '#f0f0f0', color: !targetAll ? '#fff' : enrolledStudents.length === 0 ? '#ccc' : '#666', transition: 'all 0.15s' }}>
          {tT.specificStudents || 'Specific students'}
          {!targetAll && selectedStudentIds.length > 0 && (
            <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.3)', borderRadius: 10, padding: '0 5px', fontSize: '0.75rem' }}>{selectedStudentIds.length}</span>
          )}
        </button>
      </div>

      {targetAll ? (
        <p style={{ fontSize: '0.82rem', color: '#888', margin: 0 }}>
          {enrolledStudents.length > 0
            ? `${enrolledStudents.length} ${tT.enrolledWillReceive || 'enrolled students will receive this'}`
            : (tT.noStudentsEnrolled || 'No students enrolled yet')}
        </p>
      ) : (
        <div>
          {/* Selected chips */}
          {selectedStudentIds.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', padding: '0.5rem 0.6rem', border: '1px solid #e8ecf0', borderRadius: 10, background: '#fafbff', marginBottom: '0.5rem' }}>
              {selectedStudentIds.map(id => {
                const s = enrolledStudents.find(e => e.id === id);
                if (!s) return null;
                const initials = (s.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                return (
                  <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: '#ede9fe', color: '#5B35A0', borderRadius: 20, padding: '0.2rem 0.4rem 0.2rem 0.3rem', fontSize: '0.8rem', fontWeight: 600 }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#5B35A0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 700, flexShrink: 0 }}>{initials}</span>
                    {s.name}
                    <button type="button" onClick={() => setSelectedStudentIds(prev => prev.filter(i => i !== id))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 0, lineHeight: 1, display: 'flex' }}>
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Add students button */}
          {enrolledStudents.filter(s => !selectedStudentIds.includes(s.id)).length > 0 && (
            <button type="button" onClick={() => { setStudentDropdownOpen(true); setStudentSearch(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: '1px dashed #c4b5fd', borderRadius: 8, padding: '0.4rem 0.75rem', cursor: 'pointer', color: '#7c3aed', fontSize: '0.82rem', fontWeight: 600, width: '100%', justifyContent: 'center' }}>
              <Users size={13} />
              {selectedStudentIds.length === 0 ? (tT.addStudents || 'Add students…') : (tT.addMore || 'Add more…')}
            </button>
          )}

          {selectedStudentIds.length === 0 && (
            <p style={{ fontSize: '0.75rem', color: '#f59e0b', margin: '0.5rem 0 0', textAlign: 'center' }}>⚠ {tT.selectAtLeastOne || 'Select at least one student'}</p>
          )}

          {/* Centered student picker modal */}
          {studentDropdownOpen && (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
              onClick={() => setStudentDropdownOpen(false)}
            >
              <div
                style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', maxHeight: '80vh', overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}
              >
                {/* Modal header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.25rem 0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a2e' }}>
                      {tT.addStudents || 'Add students'}
                    </h3>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: '#aaa' }}>
                      {enrolledStudents.filter(s => !selectedStudentIds.includes(s.id)).length} {tT.available || 'available'}
                    </p>
                  </div>
                  <button onClick={() => setStudentDropdownOpen(false)}
                    style={{ background: '#f5f5f5', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                    <X size={15} />
                  </button>
                </div>

                {/* Search */}
                <div style={{ padding: '0.65rem 1.25rem', borderBottom: '1px solid #f5f5f5' }}>
                  <input
                    autoFocus
                    value={studentSearch}
                    onChange={e => setStudentSearch(e.target.value)}
                    placeholder={tT.searchStudents || 'Search by name or email…'}
                    style={{ width: '100%', border: '1.5px solid #e8ecf0', borderRadius: 8, outline: 'none', fontSize: '0.88rem', padding: '0.5rem 0.75rem', boxSizing: 'border-box', background: '#fafbff' }}
                    onFocus={e => e.target.style.borderColor = '#667eea'}
                    onBlur={e => e.target.style.borderColor = '#e8ecf0'}
                  />
                </div>

                {/* Student list */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {enrolledStudents
                    .filter(s => !selectedStudentIds.includes(s.id))
                    .filter(s => !studentSearch || s.name?.toLowerCase().includes(studentSearch.toLowerCase()) || s.email?.toLowerCase().includes(studentSearch.toLowerCase()))
                    .map(s => {
                      const initials = (s.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                      return (
                        <button type="button" key={s.id}
                          onClick={() => { setSelectedStudentIds(prev => [...prev, s.id]); setStudentSearch(''); }}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.85rem', padding: '0.75rem 1.25rem', background: 'none', border: 'none', borderBottom: '1px solid #f9f9f9', cursor: 'pointer', textAlign: 'left' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                          <span style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700 }}>
                            {initials}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a2e' }}>{s.name}</div>
                            <div style={{ fontSize: '0.78rem', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.email}</div>
                          </div>
                          <Plus size={16} color="#c4b5fd" style={{ flexShrink: 0 }} />
                        </button>
                      );
                    })
                  }
                  {enrolledStudents
                    .filter(s => !selectedStudentIds.includes(s.id))
                    .filter(s => !studentSearch || s.name?.toLowerCase().includes(studentSearch.toLowerCase()) || s.email?.toLowerCase().includes(studentSearch.toLowerCase()))
                    .length === 0 && (
                    <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', padding: '2rem 1rem' }}>
                      {studentSearch ? (tT.noStudentsMatch || 'No students match your search') : (tT.allAdded || 'All students already added')}
                    </p>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.82rem', color: '#667eea', fontWeight: 600 }}>
                    {selectedStudentIds.length} {selectedStudentIds.length === 1 ? (tT.studentSingular || 'student') : (tT.studentPlural || 'students')} {tT.selected || 'selected'}
                  </span>
                  <button className="btn-primary" style={{ padding: '0.45rem 1.25rem' }} onClick={() => setStudentDropdownOpen(false)}>
                    {tT.done || 'Done'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const questionsPanel = (
    <div className="studio-card" style={{ padding: '1.5rem' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <CheckCircle size={15} color="#5B35A0" /> {tT.selectQuestions || 'Questions'}
        </span>
        {form.question_ids.length > 0 && (
          <span style={{ fontSize: '0.78rem', background: '#ede9fe', color: '#5B35A0', borderRadius: 10, padding: '2px 8px', fontWeight: 700 }}>
            {form.question_ids.length} {tT.selected || 'selected'}
          </span>
        )}
      </h3>
      {loadingQ ? (
        <div style={{ textAlign: 'center', padding: '1.5rem' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>
      ) : questions.length === 0 ? (
        <p style={{ color: '#aaa', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>{tT.noApprovedQuestions || 'No approved questions found. Approve questions in the Course Editor first.'}</p>
      ) : (
        <div style={{ border: '1px solid #e8ecf0', borderRadius: 10, overflow: 'hidden' }}>
          {questions.map((q, i) => {
            const selected = form.question_ids.includes(q.id);
            return (
              <label key={q.id} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', alignItems: 'start', gap: '0.5rem', padding: '0.65rem 0.9rem', cursor: 'pointer', background: selected ? '#f0f3ff' : i % 2 === 0 ? '#fafbff' : '#fff', borderBottom: '1px solid #f0f0f0' }}>
                <input type="checkbox" checked={selected} onChange={() => toggleQuestion(q.id)}
                  style={{ marginTop: 4, accentColor: '#5B35A0', width: 14, height: 14 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.15rem', alignItems: 'center' }}>
                    <span className={`badge ${q.type === 'multiple_choice' ? 'badge-blue' : q.type === 'numeric' ? 'badge-orange' : 'badge-purple'}`} style={{ fontSize: '0.66rem', padding: '1px 5px' }}>
                      {q.type === 'multiple_choice' ? 'MC' : q.type === 'numeric' ? 'NUM' : 'OE'}
                    </span>
                    <span style={{ fontSize: '0.66rem', color: '#f59e0b', lineHeight: 1 }}>{'★'.repeat(q.difficulty || 1)}</span>
                  </div>
                  <span style={{ fontSize: '0.84rem', color: '#333', lineHeight: 1.4 }}>{q.text}</span>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Action bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className="studio-back-btn" onClick={onClose}>
          <ArrowLeft size={14} /> {tA.cancel || 'Cancel'}
        </button>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', flex: 1 }}>
          {initial?.id ? (tT.editAssignment || 'Edit Assignment') : (tT.createAssignment || 'Create Assignment')}
        </h2>
        <button className="btn-primary" style={{ padding: '0.55rem 1.5rem' }}
          onClick={() => onSave({ ...form, attachments, time_limit_minutes: form.time_limit_minutes !== '' ? form.time_limit_minutes : null, due_date: form.due_date || null, student_ids: targetAll ? [] : selectedStudentIds })}
          disabled={!canSave}>
          {initial?.id ? (tT.saveChanges || 'Save Changes') : (tT.createAssignment || 'Create Assignment')}
        </button>
      </div>

      {/* Two-column grid — auto collapses to 1 col on mobile */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem', alignItems: 'start' }}>
        {detailsPanel}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {studentPanel}
          {questionsPanel}
        </div>
      </div>
    </div>
  );
}

export function AssignmentManagerTab() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tT = t.teacher || {};

  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [toast, setToast] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const showToast = (msg, type = '') => {
    setToast({ msg, type });
    setTimeout(() => setToast(''), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([listAssignments(), listCourses()]);
      setAssignments(a);
      setCourses(c);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editingAssignment?.id) {
        await updateAssignment(editingAssignment.id, form);
        showToast(tT.assignmentUpdated || 'Assignment updated', 'success');
      } else {
        await createAssignment(form);
        showToast(tT.assignmentCreated || 'Assignment created', 'success');
      }
      setShowForm(false);
      setEditingAssignment(null);
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleTogglePublish = async (a) => {
    try {
      const res = await togglePublishAssignment(a.id);
      showToast(res.is_published ? (tT.assignmentPublished || 'Assignment published!') : (tT.assignmentUnpublished || 'Assignment unpublished.'), 'success');
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

  if (showForm && courses.length > 0) {
    return (
      <>
        <AssignmentForm
          courses={courses}
          initial={editingAssignment}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingAssignment(null); }}
        />
        {toast && <div className={`studio-toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn-primary" onClick={() => { setEditingAssignment(null); setShowForm(true); }}>
          <Plus size={16} /> {t.teacher?.createAssignment || 'Create Assignment'}
        </button>
      </div>
      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : assignments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Edit2 size={48} /></div>
          <h3>{t.teacher?.noAssignments || 'No assignments yet'}</h3>
          <p>{t.teacher?.createFirstAssignment || 'Create your first assignment from approved questions'}</p>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={16} /> {t.teacher?.createAssignment || 'Create Assignment'}
          </button>
        </div>
      ) : (() => {
        // Group by course
        const groups = [];
        const seen = new Map();
        assignments.forEach(a => {
          const key = a.course_id || '__none__';
          const label = a.course_title || t.teacher?.uncategorised || 'Uncategorised';
          if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ key, label, items: [] }); }
          groups[seen.get(key)].items.push(a);
        });

        const toggleGroup = (key) => setCollapsedGroups(prev => {
          const next = new Set(prev);
          next.has(key) ? next.delete(key) : next.add(key);
          return next;
        });

        return groups.map(({ key, label, items }) => {
          const collapsed = collapsedGroups.has(key);
          const liveCount = items.filter(a => a.is_published).length;
          return (
            <div key={key} style={{ marginBottom: '1rem' }}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(key)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '0.5rem 0.75rem', borderRadius: 10,
                  borderBottom: collapsed ? 'none' : '2px solid #e8ecf0',
                  marginBottom: collapsed ? 0 : '0.5rem',
                }}
              >
                <BookOpen size={14} color="#5B35A0" style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', textAlign: 'left' }}>{label}</span>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  {items.length} {tT.assignmentsCount || 'assignments'}
                  {liveCount > 0 && <span style={{ marginLeft: '0.4rem', color: '#16a34a' }}>· {liveCount} {tT.live || 'live'}</span>}
                </span>
                <ChevronDown size={14} style={{ color: '#aaa', transform: collapsed ? 'none' : 'rotate(180deg)', transition: 'transform 0.2s', flexShrink: 0 }} />
              </button>

              {/* Assignment rows */}
              {!collapsed && items.map(a => (
                <div key={a.id} className="assignment-card">
                  <div className="assignment-card-info">
                    <div className="assignment-card-title">{a.title}</div>
                    <div className="assignment-card-meta">
                      <span className={`badge ${a.type === 'test' ? 'badge-orange' : 'badge-blue'}`}>
                        {a.type === 'test' ? (t.teacher?.assignmentTypeTest || 'Test') : (t.teacher?.assignmentTypeAssignment || 'Assignment')}
                      </span>
                      {a.due_date && <span><Calendar size={12} style={{ verticalAlign: 'middle' }} /> {fmtDate(a.due_date)}</span>}
                      {a.time_limit_minutes && <span><Clock size={12} style={{ verticalAlign: 'middle' }} /> {a.time_limit_minutes}min</span>}
                      <span>{a.submission_count || 0} {t.teacher?.submissions || 'submissions'}</span>
                    </div>
                  </div>
                  <div className="assignment-card-actions">
                    <button
                      className={a.is_published ? 'btn-secondary' : 'btn-primary'}
                      style={{ padding: '0.4rem 0.8rem', fontSize: '0.82rem' }}
                      onClick={() => handleTogglePublish(a)}
                      title={a.is_published ? (tT.unpublish || 'Unpublish') : (t.publish || 'Publish')}
                    >
                      {a.is_published
                        ? <><EyeOff size={13} /> {tT.unpublish || 'Unpublish'}</>
                        : <><Send size={13} /> {t.publish || 'Publish'}</>}
                    </button>
                    <button className="btn-icon" title={t.teacher?.results || 'Results'}
                      onClick={() => navigate(`/studio/teacher/assignments/${a.id}/results`)}>
                      <BarChart2 size={16} />
                    </button>
                    <button className="btn-icon" title={t.edit || 'Edit'}
                      onClick={() => { setEditingAssignment(a); setShowForm(true); }}>
                      <Edit2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        });
      })()}

      {toast && <div className={`studio-toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

export default function AssignmentManager() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <div className="studio-subheader-left">
          <button className="studio-back-btn" onClick={() => navigate('/studio/teacher')}>
            <ArrowLeft size={15} /> {t.back || 'Back'}
          </button>
          <h1 className="studio-page-title">{t.teacher?.assignments || 'Assignments'}</h1>
        </div>
      } />
      <div className="studio-main">
        <AssignmentManagerTab />
      </div>
    </div>
  );
}
